import React, { useState } from 'react';
import type { Organization } from '../../api/organizations';
// import { permissionAPI } from '../../api/permissions'; // Not used
import { Button } from '../ui/Button/Button';
import { Badge } from '../ui/Badge/Badge';
import Modal from '../Modal/Modal';
import styles from './OrganizationCard.module.css';

import { logger } from '../../utils/logger';

interface OrganizationCardProps {
  organization: Organization;
  onEdit?: (organization: Organization) => void;
  onDelete?: (organizationId: string) => void;
  onSelect?: (organization: Organization) => void;
  isSelected?: boolean;
  showActions?: boolean;
  currentUserRole?: string;
}

export const OrganizationCard: React.FC<OrganizationCardProps> = ({
  organization,
  onEdit,
  onDelete,
  onSelect,
  isSelected = false,
  showActions = true,
  currentUserRole = 'member',
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const canEdit = currentUserRole === 'owner' || currentUserRole === 'admin';
  const canDelete = currentUserRole === 'owner';

  const handleEdit = () => {
    if (onEdit && canEdit) {
      onEdit(organization);
    }
  };

  const handleDelete = async () => {
    if (!onDelete || !canDelete) return;
    
    setIsLoading(true);
    try {
      await onDelete(organization.id);
      setShowDeleteModal(false);
    } catch (error) {
      logger.error('Failed to delete organization:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = () => {
    if (onSelect) {
      onSelect(organization);
    }
  };

  const getPlanBadgeVariant = (planType: string) => {
    switch (planType) {
      case 'enterprise':
        return 'success';
      case 'agency':
      case 'team':
        return 'warning';
      case 'pro':
      case 'professional':
        return 'primary';
      case 'starter':
        return 'info';
      case 'free':
      default:
        return 'secondary';
    }
  };

  const getStatusBadgeVariant = (isActive: boolean) => {
    return isActive ? 'success' : 'error';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      <div 
        className={`${styles.organizationCard} ${isSelected ? styles.selected : ''}`}
        onClick={handleSelect}
      >
        <div className={styles.cardHeader}>
          <div className={styles.organizationInfo}>
            <h3 className={styles.organizationName}>{organization.name}</h3>
            <p className={styles.organizationSlug}>@{organization.slug}</p>
          </div>
          <div className={styles.badges}>
            <Badge variant={getPlanBadgeVariant(organization.plan_type)}>
              {organization.plan_type.toUpperCase()}
            </Badge>
            <Badge variant={getStatusBadgeVariant(organization.is_active)}>
              {organization.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
        </div>

        <div className={styles.cardBody}>
          <div className={styles.organizationDetails}>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Billing Email:</span>
              <span className={styles.detailValue}>{organization.billing_email}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Created:</span>
              <span className={styles.detailValue}>{formatDate(organization.created_at)}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Last Updated:</span>
              <span className={styles.detailValue}>{formatDate(organization.updated_at)}</span>
            </div>
          </div>

          {organization.settings?.feature_flags && (
            <div className={styles.featureFlags}>
              <h4 className={styles.featureFlagsTitle}>Features</h4>
              <div className={styles.featureFlagsList}>
                {Object.entries(organization.settings.feature_flags).map(([key, value]) => (
                  <Badge 
                    key={key} 
                    variant={value ? 'success' : 'secondary'}
                    className={styles.featureFlag}
                  >
                    {key.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {showActions && (
          <div className={styles.cardFooter}>
            <div className={styles.actions}>
              {canEdit && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleEdit()}
                >
                  Edit
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowDeleteModal(true)}
                  className={styles.deleteButton}
                >
                  Delete
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Organization"
        size="medium"
      >
        <div className={styles.deleteModal}>
          <p className={styles.deleteWarning}>
            Are you sure you want to delete <strong>{organization.name}</strong>?
          </p>
          <p className={styles.deleteDescription}>
            This action cannot be undone. All data associated with this organization will be permanently deleted.
          </p>
          
          <div className={styles.deleteModalActions}>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleDelete}
              loading={isLoading}
              className={styles.confirmDeleteButton}
            >
              Delete Organization
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}; 